import React from "react";
import { CircularProgress, Stack } from "@mui/material";
import Chip from "@hyro-components/Chip";
import CollapsibleSection from "@hyro-components/CollapsibleSection";
import useSubscriptionTab from "./SubscriptionTab.hook";
import AccountTeamSection from "./AccountTeamSection";
import GoLiveBanner from "./GoLiveBanner";
import ProductsTable from "./ProductsTable";
import QuotaUsageSummary from "./QuotaUsageSummary";
import SubscriptionDetails from "./SubscriptionDetails";
import SubscriptionEmptyState from "./SubscriptionEmptyState";
import SubscriptionErrorState from "./SubscriptionErrorState";
import {
  ACTIVE_CHIP_STYLE,
  RENEWAL_WARNING_CHIP_STYLE
} from "./SubscriptionTab.consts";

const buildSubscriptionDetailsChips = ({ daysToRenewal, isActive, showRenewalWarning }) => {
  const chips = [];

  if (showRenewalWarning) {
    chips.push(
      <Chip
        key="renewal"
        label={`${daysToRenewal} days to renewal`}
        {...RENEWAL_WARNING_CHIP_STYLE}
      />
    );
  }

  if (isActive) {
    chips.push(<Chip key="active" label="Active" {...ACTIVE_CHIP_STYLE} />);
  }

  return chips;
};

const SubscriptionTab = ({ accountId, isAccountContext, salesforceAccountId }) => {
  const {
    currentTerm,
    teamMembers,
    interactionLimit,
    daysToRenewal,
    isActive,
    isNotStarted,
    formattedDateRange,
    showRenewalWarning,
    showGoLiveWarning,
    isLoading,
    isError,
    isEmpty
  } = useSubscriptionTab({
    accountId,
    isAccountContext,
    hasSalesforceId: !!salesforceAccountId
  });

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 400 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (isError) {
    return <SubscriptionErrorState />;
  }

  if (isEmpty) {
    return <SubscriptionEmptyState />;
  }

  const detailsChips = buildSubscriptionDetailsChips({ daysToRenewal, isActive, showRenewalWarning });

  return (
    <Stack gap={3}>
      {showGoLiveWarning && <GoLiveBanner salesforceAccountId={salesforceAccountId} />}
      <AccountTeamSection teamMembers={teamMembers} />
      <CollapsibleSection title="Subscription details" headerRight={detailsChips}>
        <SubscriptionDetails term={currentTerm} salesforceAccountId={salesforceAccountId} />
      </CollapsibleSection>
      <CollapsibleSection title={`Products and packages (${currentTerm.products.length})`}>
        <ProductsTable products={currentTerm.products} />
      </CollapsibleSection>
      <CollapsibleSection title="Current Quota usage" subtitle={formattedDateRange}>
        <QuotaUsageSummary
          term={currentTerm}
          interactionLimit={interactionLimit}
          isNotStarted={isNotStarted}
        />
      </CollapsibleSection>
    </Stack>
  );
};

export default SubscriptionTab;
